import React, {useEffect} from "react"
import {AccountsColumns, AllowedAccountsColumns} from "./Accounts.types"
import {DataTable, DataTableBody, DataTableCell, DataTableHead, DataTableRow} from "../DataTable/DataTable"
import {useIsOrgUser} from "../../services/auth"
import ReactTooltip from "react-tooltip"
import Icon from "../Icon/Icon"
import AccountRow from "./AccountRow"
import {isProdEnv} from "../../utilities/environment"
import PagingNavBar from "../PagingNavBar/PagingNavBar"
import {Meta, Resource} from "../../resources/common"
import {Account} from "../../resources/account"

interface AccountsTableProps {
	accounts: Array<Account>
	hasResults: boolean
	hasPrev: boolean
	hasNext: boolean
	prev: () => void
	next: () => void
	isUsingPaging: boolean
	include?: Resource[]
	includedColumns: Array<AllowedAccountsColumns>
	meta?: Meta
	fullHeight?: boolean
	enableTutorial?: boolean
	isFiltering?: boolean
	sortFunction: (sortParam: string) => void
	sortBy: string
	refresh?: () => void
}

export default function AccountsTable({
	accounts,
	includedColumns,
	hasPrev,
	hasNext,
	prev,
	next,
	sortFunction,
	sortBy,
	hasResults,
	isUsingPaging,
	include,
	meta,
	fullHeight,
	enableTutorial,
	isFiltering,
	refresh,
}: AccountsTableProps) {
	const noContent = accounts.length === 0
	const tutorialCallButton = document.querySelector(".tutorial-call-button")
	const isOrgUser = useIsOrgUser()

	useEffect(() => {
		if (!noContent && tutorialCallButton) {
			tutorialCallButton.classList.add("tutorial-call-button-with-animation")
		}
	}, [])

	return (
		<div className={"accounts-table"}>
			<DataTable
				isEmpty={noContent}
				fullHeight={fullHeight}
				stickyAction={fullHeight}
				noContentText={
					!isFiltering ? (
						<>
							No{" "}
							<a className="doc-link" href="https://docs.unit.co/deposit-accounts/" target="_blank" rel="noreferrer">
								{" "}
								accounts
							</a>{" "}
							yet
						</>
					) : (
						"No accounts found"
					)
				}
				noContentCtaButton={
					!isProdEnv() &&
					enableTutorial &&
					isOrgUser && (
						<button
							className="tutorial-cta-button"
							data-tracking-label="tutorial-accounts-cta"
							onClick={(e) => {
								const target = e.target as HTMLButtonElement
								target.classList.add("tutorial-cta-button-was-used")

								if (tutorialCallButton) {
									ReactTooltip.show(tutorialCallButton)
								}
							}}
						>
							<Icon icon={"programming-code--programing-apps-websites"} color="white" />
							Test Create Account API
						</button>
					)
				}
			>
				<DataTableHead>
					<DataTableRow>
						{Object.entries(includedColumns).map((column) => {
							if (column[1] === AccountsColumns.createdAt) {
								return (
									<DataTableCell
										clickable
										onClick={() => sortFunction("createdAt")}
										key={column[0]}
										sortable={sortBy.includes("createdAt")}
										sortApplied={sortBy === "createdAt"}
									>
										{column[1]}
									</DataTableCell>
								)
							}
							if (column[1] === AccountsColumns.updatedAt) {
								return (
									<DataTableCell
										clickable
										onClick={() => sortFunction("updatedAt")}
										key={column[0]}
										sortable={sortBy.includes("updatedAt")}
										sortApplied={sortBy === "updatedAt"}
									>
										{column[1]}
									</DataTableCell>
								)
							}
							if (column[1] === AccountsColumns.balance) {
								return (
									<DataTableCell
										clickable
										onClick={() => sortFunction("balance")}
										key={column[0]}
										sortable={sortBy.includes("balance")}
										sortApplied={sortBy === "balance"}
									>
										{column[1]}
									</DataTableCell>
								)
							}
							if (column[1] === AccountsColumns.type) {
								return (
									<DataTableCell
										clickable
										onClick={() => sortFunction("type")}
										key={column[0]}
										sortable={sortBy.includes("type")}
										sortApplied={sortBy === "type"}
									>
										{column[1]}
									</DataTableCell>
								)
							}

							return <DataTableCell key={column[0]}>{column[1]}</DataTableCell>
						})}
					</DataTableRow>
				</DataTableHead>
				<DataTableBody>
					{accounts.map((a) => (
						<AccountRow include={include} includedColumns={includedColumns} account={a} key={a.id} refresh={refresh} />
					))}
				</DataTableBody>
			</DataTable>
			<PagingNavBar
				hasResults={hasResults}
				hasPrev={hasPrev}
				hasNext={hasNext}
				prev={prev}
				next={next}
				isShow={isUsingPaging}
				meta={meta}
			/>
		</div>
	)
}
